import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { tmgClearAllIcon, tmgParticipantNoteInfoIcon } from 'assets/assets'
import STRINGS from 'utils/strings'
import { TIMED_MATCHING_GRID_KEY_SLOT_COUNT } from 'utils/constants'
import {
  countPlacedItems,
  generateBalancedItemSequence,
  getEmptyItemSequence,
  getGridCellCount,
  getOverusedBalanceDigits,
  getSymbolBalanceCounts,
  getSymbolBalanceTargets,
  getSymbolKeyEntry,
} from 'utils/timedMatchingGrid'
import TimedMatchingGridSymbol from './TimedMatchingGridSymbol'

const BALANCE_WARNING_TRIANGLE_PATH = [
  'M14.4866 12.0005L9.15329 2.66714C9.037 2.46194 8.86836 2.29127 8.66457 2.17252C8.46078 2.05378 8.22915 1.99121',
  '7.99329 1.99121C7.75743 1.99121 7.52579 2.05378 7.322 2.17252C7.11822 2.29127 6.94958 2.46194 6.83329',
  '2.66714L1.49995 12.0005C1.38241 12.204 1.32077 12.4351 1.32129 12.6701C1.32181 12.9052 1.38447 13.136 1.50292',
  '13.339C1.62136 13.5421 1.79138 13.7102 1.99575 13.8264C2.20011 13.9425 2.43156 14.0026 2.66662',
  '14.0005H13.3333C13.5672 14.0002 13.797 13.9385 13.9995 13.8213C14.202 13.7042 14.3701 13.5359 14.487',
  '13.3332C14.6038 13.1306 14.6653 12.9007 14.6653 12.6668C14.6652 12.4329 14.6036 12.2031 14.4866 12.0005Z',
].join(' ')

const BalanceWarningIcon = () => (
  <svg
    className='tmg-balance-warning-icon'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'>
    <path
      d={BALANCE_WARNING_TRIANGLE_PATH}
      stroke='#9A6510'
      strokeWidth='1.33333'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M8 6V8.66667' stroke='#9A6510' strokeWidth='1.33333' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M8 11.333H8.00667' stroke='#9A6510' strokeWidth='1.33333' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

/**
 * Full-screen two-pane manual item-sequence editor (GOLD-12177).
 * Opened as a separate screen from the instrument builder when randomization is off.
 */
const ItemSequenceEditor = ({
  details,
  disabled,
  errors,
  onChangeSequence,
  onClose,
}) => {
  const {
    grid_rows: rows,
    grid_columns: columns,
    symbol_key: symbolKey = [],
    item_sequence: itemSequence,
  } = details

  const total = getGridCellCount(rows, columns)
  const [sequence, setSequence] = useState(() => {
    if (Array.isArray(itemSequence) && itemSequence.length === total) return itemSequence
    return getEmptyItemSequence(rows, columns)
  })
  const [focusIndex, setFocusIndex] = useState(0)
  const gridRef = useRef(null)

  const placed = countPlacedItems(sequence)
  const targets = getSymbolBalanceTargets(total)
  const counts = getSymbolBalanceCounts(sequence)
  const overusedDigits = getOverusedBalanceDigits(sequence, total)
  const cursorRow = Math.floor(focusIndex / columns) + 1
  const cursorCol = (focusIndex % columns) + 1

  useEffect(() => {
    if (focusIndex >= total) setFocusIndex(Math.max(0, total - 1))
  }, [total, focusIndex])

  useEffect(() => {
    if (gridRef.current) gridRef.current.focus()
  }, [])

  const commitSequence = useCallback(
    next => {
      setSequence(next)
      onChangeSequence(next)
    },
    [onChangeSequence],
  )

  const updateAt = useCallback(
    (index, value) => {
      const next = [...sequence]
      next[index] = value
      commitSequence(next)
    },
    [sequence, commitSequence],
  )

  const moveFocus = useCallback(
    (deltaRow, deltaCol) => {
      const row = Math.floor(focusIndex / columns)
      const col = focusIndex % columns
      const nextRow = Math.min(rows - 1, Math.max(0, row + deltaRow))
      const nextCol = Math.min(columns - 1, Math.max(0, col + deltaCol))
      setFocusIndex(nextRow * columns + nextCol)
    },
    [focusIndex, rows, columns],
  )

  const onKeyDown = e => {
    if (disabled) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      moveFocus(0, -1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      moveFocus(0, 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveFocus(-1, 0)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveFocus(1, 0)
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      updateAt(focusIndex, null)
    } else if (/^[1-9]$/.test(e.key)) {
      e.preventDefault()
      updateAt(focusIndex, Number(e.key))
      if (focusIndex < total - 1) setFocusIndex(focusIndex + 1)
    } else if (e.key === 'Escape' && onClose) {
      onClose()
    }
  }

  const onClearAll = () => {
    commitSequence(getEmptyItemSequence(rows, columns))
    setFocusIndex(0)
  }

  const onGenerate = () => {
    commitSequence(generateBalancedItemSequence(rows, columns))
    setFocusIndex(0)
  }

  const progressPct = total ? Math.round((placed / total) * 100) : 0

  return (
    <div className='tmg-item-sequence-screen'>
      <div className='tmg-item-sequence-main'>
        <div className='tmg-item-sequence-header'>
          <div className='tmg-item-sequence-title-block'>
            <h2 className='tmg-item-sequence-title'>{STRINGS.tmgItemSequenceTitle}</h2>
            <p className='tmg-item-sequence-instructions'>{STRINGS.tmgItemSequenceInstructions}</p>
          </div>
          <div className='tmg-item-sequence-actions'>
            <Button
              link
              no-margin
              className='tmg-sequence-action-link muted'
              content={
                <>
                  <span className='tmg-action-icon'>{tmgClearAllIcon}</span>
                  {STRINGS.tmgClearAll}
                </>
              }
              onClick={onClearAll}
              disabled={disabled}
            />
            {onClose && (
              <Button
                link
                no-margin
                className='tmg-sequence-action-link'
                content={STRINGS.tmgSaveItemSequence}
                onClick={onClose}
              />
            )}
          </div>
        </div>

        <div className='tmg-key-reference'>
          <div className='tmg-key-reference-main'>
            <h5 className='tmg-key-reference-label'>{STRINGS.tmgKeyReferenceTitle}</h5>
            <div className='tmg-key-preview-row compact'>
              {Array.from({ length: TIMED_MATCHING_GRID_KEY_SLOT_COUNT }, (_, i) => {
                const digit = i + 1
                return (
                  <div key={digit} className='tmg-key-preview-cell'>
                    <div className='tmg-key-preview-symbol'>
                      <TimedMatchingGridSymbol
                        entry={getSymbolKeyEntry(symbolKey, digit)}
                        alt={`Key ${digit}`}
                      />
                    </div>
                    <div className='tmg-key-preview-digit'>{digit}</div>
                  </div>
                )
              })}
            </div>
          </div>
          <p className='tmg-helper'>{STRINGS.tmgPressDigitToPlace}</p>
        </div>

        <div className='tmg-sequence-grid-wrap'>
          <div className='tmg-sequence-row-labels' aria-hidden='true'>
            {Array.from({ length: rows }, (_, r) => (
              <div key={`row-label-${r}`} className='tmg-sequence-row-label'>
                {r + 1}
              </div>
            ))}
          </div>
          <div
            className='tmg-sequence-grid'
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
            tabIndex={0}
            ref={gridRef}
            onKeyDown={onKeyDown}
            role='grid'
            aria-label='Item sequence grid'>
            {sequence.map((value, index) => {
              const entry = value ? getSymbolKeyEntry(symbolKey, value) : null
              const isFocused = index === focusIndex
              return (
                <button
                  type='button'
                  // Sequence cells are positional; index is the stable identity for this grid.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`cell-${index}`}
                  className={`tmg-sequence-cell${isFocused ? ' focused' : ''}${value ? ' filled' : ''}`}
                  onClick={() => {
                    setFocusIndex(index)
                    if (gridRef.current) gridRef.current.focus()
                  }}
                  disabled={disabled}>
                  <div className='tmg-sequence-symbol'>
                    {entry ? (
                      <TimedMatchingGridSymbol entry={entry} alt={`Symbol ${value}`} />
                    ) : (
                      <span className='tmg-placeholder'>
                        {isFocused && !value ? <span className='tmg-caret' /> : value || '—'}
                      </span>
                    )}
                  </div>
                  <div className='tmg-sequence-answer' aria-hidden='true' />
                </button>
              )
            })}
          </div>
        </div>
        <p className='tmg-helper tmg-participant-answer-note'>
          <span className='tmg-participant-answer-note-icon'>{tmgParticipantNoteInfoIcon}</span>
          <span>{STRINGS.tmgParticipantAnswerNote}</span>
        </p>
      </div>

      <aside className='tmg-item-sequence-sidebar'>
        <div className='tmg-progress-panel'>
          <h5 className='tmg-panel-title'>{STRINGS.tmgProgressTitle}</h5>
          <p className='tmg-progress-count'>
            <span className='tmg-progress-count-value'>{placed}</span> of {total} placed
          </p>
          <div className='tmg-progress-bar'>
            <div className='tmg-progress-fill' style={{ width: `${progressPct}%` }} />
          </div>
          <p className='tmg-helper'>
            Cursor at row <span className='tmg-num'>{cursorRow}</span>, item{' '}
            <span className='tmg-num'>{cursorCol}</span>. The form can&apos;t be published until all{' '}
            <span className='tmg-num'>{total}</span> are placed.
          </p>
          {errors?.tmgItemSequenceErr && <p className='tmg-error'>{errors.tmgItemSequenceErr}</p>}
        </div>

        <div className='tmg-balance-panel'>
          <h5 className='tmg-panel-title'>{STRINGS.tmgBalanceCheckTitle}</h5>
          <div className='tmg-balance-list'>
            {Array.from({ length: TIMED_MATCHING_GRID_KEY_SLOT_COUNT }, (_, i) => {
              const digit = i + 1
              const entry = getSymbolKeyEntry(symbolKey, digit)
              const isOver = overusedDigits.includes(digit)
              return (
                <div key={digit} className={`tmg-balance-item${isOver ? ' is-over' : ''}`}>
                  {entry ? (
                    <TimedMatchingGridSymbol entry={entry} alt='' />
                  ) : (
                    <span className='tmg-balance-digit'>{digit}</span>
                  )}
                  <span className='tmg-balance-count'>
                    {counts[digit]} / {targets[digit]}
                    {isOver ? ` ${STRINGS.tmgBalanceOver}` : ''}
                  </span>
                </div>
              )
            })}
          </div>
          {overusedDigits.length > 0 && (
            <div className='tmg-balance-warning' role='status'>
              <BalanceWarningIcon />
              <p>
                <span className='tmg-balance-warning-lead'>
                  <span className='tmg-balance-warning-symbols'>
                    {overusedDigits.map((digit, index) => {
                      const entry = getSymbolKeyEntry(symbolKey, digit)
                      return (
                        <React.Fragment key={digit}>
                          {index > 0 ? ', ' : ''}
                          {entry ? (
                            <TimedMatchingGridSymbol entry={entry} alt={`symbol ${digit}`} />
                          ) : (
                            <span className='tmg-balance-warning-digit'>{digit}</span>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </span>{' '}
                  {STRINGS.tmgBalanceOverWarningLead}
                </span>{' '}
                {STRINGS.tmgBalanceOverWarningKeep} {STRINGS.tmgBalanceOverWarningTail}
              </p>
            </div>
          )}
        </div>

        <div className='tmg-item-sequence-sidebar-actions'>
          <Button content={STRINGS.tmgGenerateForMe} onClick={onGenerate} disabled={disabled} />
        </div>
      </aside>
    </div>
  )
}

ItemSequenceEditor.propTypes = {
  details: PropTypes.shape({
    grid_rows: PropTypes.number,
    grid_columns: PropTypes.number,
    symbol_key: PropTypes.arrayOf(PropTypes.shape({})),
    item_sequence: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    tmgItemSequenceErr: PropTypes.string,
  }),
  onChangeSequence: PropTypes.func.isRequired,
  onClose: PropTypes.func,
}

export default ItemSequenceEditor
